import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import { Add, Refresh, AccountTree, HelpOutline } from '@mui/icons-material';
import {
  PageHeader,
  FilterBar,
  FilterSpacer,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  TypePill,
  TableEmptyState,
} from '../../components/common/pageKit';
import assetService, { AssetBlock, AssetBlockBalance, BlockClass, BLOCK_CLASSES } from '../../services/assetService';
import { fmtMoney, fmtPct, BLOCK_CLASS_LABEL, currentFinancialYear, financialYearOptions, tintForCategory } from './assetFormat';

// ---------------------------------------------------------------------------
// New Block dialog
// ---------------------------------------------------------------------------

const NewBlockDialog: React.FC<{ open: boolean; onClose: () => void; onSaved: () => void }> = ({
  open,
  onClose,
  onSaved,
}) => {
  const [blockName, setBlockName] = useState('');
  const [blockClass, setBlockClass] = useState<BlockClass>('PLANT_MACHINERY');
  const [ratePct, setRatePct] = useState('15');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setBlockName('');
      setBlockClass('PLANT_MACHINERY');
      setRatePct('15');
      setError(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!blockName.trim()) {
      setError('Block name is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await assetService.createBlock({ blockName: blockName.trim(), blockClass, ratePct: Number(ratePct) || 0 });
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create block');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AccountTree color="primary" />
        New Income Tax Block
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Block Name"
            required
            size="small"
            fullWidth
            value={blockName}
            onChange={(e) => setBlockName(e.target.value)}
            disabled={saving}
          />
          <TextField
            select
            label="Block Class"
            size="small"
            fullWidth
            value={blockClass}
            onChange={(e) => setBlockClass(e.target.value as BlockClass)}
            disabled={saving}
          >
            {BLOCK_CLASSES.map((c) => (
              <MenuItem key={c} value={c}>
                {BLOCK_CLASS_LABEL[c]}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Depreciation Rate (%)"
            type="number"
            size="small"
            fullWidth
            value={ratePct}
            onChange={(e) => setRatePct(e.target.value)}
            helperText="Statutory Income Tax block rate, e.g. 15% for most plant & machinery"
            disabled={saving}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving || !blockName.trim()}>
          {saving ? 'Creating…' : 'Create Block'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const AssetBlocksPage: React.FC = () => {
  const [blocks, setBlocks] = useState<AssetBlock[]>([]);
  const [blocksLoading, setBlocksLoading] = useState(true);

  const [financialYear, setFinancialYear] = useState(currentFinancialYear());
  const [balances, setBalances] = useState<AssetBlockBalance[]>([]);
  const [balancesLoading, setBalancesLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [newBlockOpen, setNewBlockOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const fetchBlocks = useCallback(() => {
    setBlocksLoading(true);
    assetService
      .getBlocks()
      .then((r) => setBlocks(r.blocks))
      .catch((err: any) => setError(err.message || 'Failed to load blocks'))
      .finally(() => setBlocksLoading(false));
  }, []);

  const fetchBalances = useCallback(() => {
    setBalancesLoading(true);
    assetService
      .getBlockBalances(financialYear)
      .then((r) => setBalances(r.balances))
      .catch((err: any) => setError(err.message || 'Failed to load block balances'))
      .finally(() => setBalancesLoading(false));
  }, [financialYear]);

  useEffect(() => {
    fetchBlocks();
  }, [fetchBlocks]);

  useEffect(() => {
    fetchBalances();
  }, [fetchBalances]);

  const fyOptions = financialYearOptions(6, 1);

  return (
    <Box>
      <PageHeader
        title="Income Tax Blocks"
        subtitle="Block-of-assets depreciation under the Income Tax Act — memorandum computation, not posted to the GL"
        actions={
          <Button variant="contained" startIcon={<Add />} size="small" onClick={() => setNewBlockOpen(true)}>
            New Block
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
        Blocks &amp; Rates
      </Typography>
      <TableSurface>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 480 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Block</TableCell>
                <TableCell sx={headerCellSx}>Class</TableCell>
                <TableCell sx={headerCellSx} align="right">Rate</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {blocksLoading ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 5, border: 0 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : blocks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} sx={{ border: 0 }}>
                    <TableEmptyState title="No blocks defined" hint="Click 'New Block' to define the first Income Tax block." />
                  </TableCell>
                </TableRow>
              ) : (
                blocks.map((b) => (
                  <TableRow key={b.id} sx={bodyRowSx}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{b.block_name}</Typography>
                    </TableCell>
                    <TableCell>
                      <TypePill label={BLOCK_CLASS_LABEL[b.block_class]} tint={tintForCategory(b.id, b.block_class)} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtPct(b.rate_pct)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableSurface>

      <Box sx={{ mt: 4 }}>
        <FilterBar>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Block Balances
          </Typography>
          <TextField
            select
            size="small"
            label="Financial Year"
            value={financialYear}
            onChange={(e) => setFinancialYear(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            {fyOptions.map((fy) => (
              <MenuItem key={fy} value={fy}>
                {fy}
              </MenuItem>
            ))}
          </TextField>
          <FilterSpacer />
          <IconButton onClick={fetchBalances} disabled={balancesLoading}>
            <Refresh />
          </IconButton>
        </FilterBar>

        <TableSurface>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 980 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Block</TableCell>
                  <TableCell sx={headerCellSx} align="right">Rate</TableCell>
                  <TableCell sx={headerCellSx} align="right">Opening WDV</TableCell>
                  <TableCell sx={headerCellSx} align="right">Additions (Full Rate)</TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      Additions (Half Rate)
                      <Tooltip title="Assets put to use fewer than 180 days before the end of this financial year get only half the block rate in year one — the statutory 180-day rule, not an arbitrary split.">
                        <HelpOutline sx={{ fontSize: 14, color: 'text.disabled' }} />
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell sx={headerCellSx} align="right">Deletions</TableCell>
                  <TableCell sx={headerCellSx} align="right">Depreciation</TableCell>
                  <TableCell sx={headerCellSx} align="right">Closing WDV</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {balancesLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6, border: 0 }}>
                      <CircularProgress size={26} />
                    </TableCell>
                  </TableRow>
                ) : balances.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ border: 0 }}>
                      <TableEmptyState
                        title="No block balances for this financial year"
                        hint="Post an Income Tax depreciation run for this FY from the Depreciation Workbench."
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  balances.map((b) => (
                    <TableRow key={b.id} sx={bodyRowSx}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{b.block_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{BLOCK_CLASS_LABEL[b.block_class]}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtPct(b.rate_pct)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(b.opening_wdv)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(b.additions_full_rate)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(b.additions_half_rate)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(b.deletions)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmtMoney(b.depreciation_amount)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(b.closing_wdv)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TableSurface>
      </Box>

      <NewBlockDialog
        open={newBlockOpen}
        onClose={() => setNewBlockOpen(false)}
        onSaved={() => {
          setToast({ message: 'Block created', severity: 'success' });
          fetchBlocks();
        }}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default AssetBlocksPage;
